import type { ListQuery } from "@erp/shared";
import type { ZodType } from "zod";
import { translateFieldError, type T } from "./i18n";

export const LIST_DEFAULTS = { offset: 0, limit: 100, archived: "exclude", dir: "asc" } as const satisfies Partial<ListQuery>;

/** Fills the list-query defaults the API schema applies, since Eden types the parsed (output) shape. */
export function listQuery<P extends Partial<ListQuery>>(params: P) {
  return { ...LIST_DEFAULTS, ...params };
}

/** `path.to.field → i18n key` for every issue, first issue wins. */
export function schemaErrors(schema: ZodType, value: unknown): Record<string, string> {
  const result = schema.safeParse(value);
  if (result.success) return {};
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) errors[issue.path.join(".")] ??= issue.message;
  return errors;
}

export type ErrorMap = Record<string, string>;

/** Translated error for a field path, or undefined. */
export function errorAt(t: T, errors: ErrorMap, path: string): string | undefined {
  return translateFieldError(t, errors[path]);
}

export interface AddressDraft {
  line1: string;
  line2: string;
  postalCode: string;
  city: string;
  country: string;
}

export const emptyAddress = (): AddressDraft => ({ line1: "", line2: "", postalCode: "", city: "", country: "FR" });

export function addressOrNull(draft: AddressDraft) {
  const filled = [draft.line1, draft.line2, draft.postalCode, draft.city].some((v) => v.trim() !== "");
  return filled ? draft : null;
}

export function addressDraft(
  address: { line1: string; line2?: string | null; postalCode: string; city: string; country: string } | null,
): AddressDraft {
  return address
    ? {
        line1: address.line1,
        line2: address.line2 ?? "",
        postalCode: address.postalCode,
        city: address.city,
        country: address.country,
      }
    : emptyAddress();
}
